'use client';

import { useMutation } from '@tanstack/react-query';
import { useLocale, useTranslations } from 'next-intl';
import { ComponentProps, createContext, MouseEvent, ReactNode, useContext } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import { QueryApi } from '@/features/query/api';
import { EventService } from '@/shared/lib/event-service';
import { isEmailValid } from '@/shared/lib/validations';
import { Language } from '@/shared/model/Language';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import { OrderMageBranding } from '@/shared/ui/ordermage-branding';
import './auth-modal.scss';

type FormValues = {
    email: string;
    password: string;
    acceptTerms: boolean;
};

type Props = ModalProps & {
    children?: ReactNode;
};

type RegisterModalContextValue = {
    form: UseFormReturn<FormValues>;
    onClose: ModalProps['onClose'];
};

const RegisterModalContext = createContext<RegisterModalContextValue | null>(null);

const useRegisterModalContext = () => {
    const context = useContext(RegisterModalContext);
    if (!context) {
        throw new Error('RegisterModal components must be used within RegisterModal.');
    }
    return context;
};

const RegisterModalProvider = ({ children, ...props }: Props) => {
    const form = useForm<FormValues>({
        defaultValues: {
            email: '',
            password: '',
        },
    });

    return (
        <RegisterModalContext.Provider
            value={{
                form,
                onClose: props.onClose,
            }}
        >
            {children}
        </RegisterModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const RegisterModalTitle = () => {
    const t = useTranslations();
    return <>{t('REGISTER_MODAL.TITLE')}</>;
};

type FormProps = ComponentProps<typeof Layout>;
const RegisterModalForm = ({ children, media, ...props }: FormProps) => {
    const t = useTranslations();
    const locale = useLocale();
    const { form, onClose } = useRegisterModalContext();
    const registerMutation = useMutation(QueryApi.Customer.mutations.Register);

    const handleSubmit = async ({ email, password }: FormValues) => {
        const response = await registerMutation.mutateAsync({
            email,
            password,
            language: locale.toUpperCase().replace('-', '_') as Language,
            timezone: 'Europe/Prague',
        });
        if (response.result === 'OK') {
            onClose();
            return;
        }
        toast(({ closeToast }) => (
            <Alert heading={t('ERRORS.EMAIL_ALREADY_EXISTS')} type="error" onClose={closeToast} />
        ));
    };

    return (
        <Form {...form} noValidate onSubmit={form.handleSubmit(handleSubmit)} className="auth-modal__form">
            <Layout media={media} {...props}>
                {children}
            </Layout>
        </Form>
    );
};

const RegisterModalEmailField = () => {
    const t = useTranslations();
    return (
        <Form.TextField
            data-testid="register-modal-email-field"
            name="email"
            label="E-mail"
            type="email"
            rules={{
                required: true,
                validate: (value) => isEmailValid(value) || t('ERRORS.INVALID_EMAIL'),
            }}
        />
    );
};

const RegisterModalPasswordField = () => {
    const t = useTranslations();
    return (
        <Form.PasswordField
            data-testid="register-modal-password-field"
            name="password"
            label={t('WORDS.PASSWORD')}
            rules={{
                required: true,
            }}
            isCheckerEnabled
        />
    );
};

const RegisterModalAcceptTermsCheckbox = () => {
    const t = useTranslations();
    const { form } = useRegisterModalContext();

    const handleShowTerms = (e?: MouseEvent<HTMLSpanElement>) => {
        e?.preventDefault();
        EventService.trigger('register-terms-and-conditions:view-detail', {
            onAccept: () => {
                form.setValue('acceptTerms', true, {
                    shouldDirty: true,
                    shouldTouch: true,
                });
            },
        });
    };

    return (
        <Form.Checkbox
            data-testid="register-modal-accept-terms-checkbox"
            name="acceptTerms"
            rules={{
                required: true,
            }}
            label={t.rich('REGISTER_MODAL.CHECKBOX_ACCEPT_TERMS', {
                terms: (chunks) => (
                    <span role="button" onClick={handleShowTerms} className="auth-modal__checkbox-btn">
                        {chunks}
                    </span>
                ),
            })}
        />
    );
};

const RegisterModalSubmitButton = () => {
    const t = useTranslations();
    return (
        <Button data-testid="register-modal-submit-button" className="auth-modal__button" type="submit">
            {t('REGISTER_MODAL.SUBMIT_BUTTON')}
        </Button>
    );
};

const RegisterModalLoginText = () => {
    const t = useTranslations();
    const handleShowLogin = () => {
        ModalService.showPrefab('LoginModal', {}, { position: 'replace' });
    };
    return (
        <span className="auth-modal__register-text">
            {t.rich('REGISTER_MODAL.LOGIN', {
                button: (chunks) => (
                    <button type="button" onClick={handleShowLogin} className="auth-modal__checkbox-btn">
                        {chunks}
                    </button>
                ),
            })}
        </span>
    );
};

const RegisterModalBottomPanel = () => {
    const { form } = useRegisterModalContext();
    const handleShowTerms = (e?: MouseEvent<HTMLSpanElement>) => {
        e?.preventDefault();
        EventService.trigger('register-terms-and-conditions:view-detail', {
            onAccept: () => {
                form.setValue('acceptTerms', true, {
                    shouldDirty: true,
                    shouldTouch: true,
                });
            },
        });
    };
    return <OrderMageBranding onLogoClick={handleShowTerms} />;
};

export const RegisterModalWrapper = ({ children, ...props }: Props) => {
    return (
        <RegisterModalProvider {...props}>
            <Modal
                {...props}
                wrapperProps={{
                    className: 'auth-modal',
                    containerProps: {
                        className: 'auth-modal__container',
                    },
                    'data-testid': 'register-modal',
                }}
            >
                {children}
            </Modal>
        </RegisterModalProvider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const RegisterModalProviderComponent = ({ children, ...props }: Props) => (
    <RegisterModalProvider {...props}>{children}</RegisterModalProvider>
);

export {
    RegisterModalWrapper as Wrapper,
    RegisterModalProviderComponent as Provider,
    RegisterModalTitle as Title,
    RegisterModalForm as Form,
    RegisterModalEmailField as EmailField,
    RegisterModalPasswordField as PasswordField,
    RegisterModalAcceptTermsCheckbox as AcceptTermsCheckbox,
    RegisterModalSubmitButton as SubmitButton,
    RegisterModalLoginText as LoginText,
    RegisterModalBottomPanel as BottomPanel,
};

export { RegisterModalWrapper as RegisterModal };
